import React from 'react';
import { Calendar, Mail, CheckSquare } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <Calendar className="h-12 w-12 text-burnt-orange" />,
      title: "Calendar Control",
      subtitle: "Fewer interruptions. Cleaner priorities.",
      description: "Your day stops being public property. We screen meetings, protect deep work time, and bring structure back to your schedule.",
      features: [
        "Meeting screening and prioritization",
        "Deep work time protection",
        "Schedule structure optimization",
        "Time block management"
      ]
    },
    {
      icon: <Mail className="h-12 w-12 text-burnt-orange" />,
      title: "Inbox Clarity",
      subtitle: "Less noise. Better decisions.",
      description: "We sort, flag, draft, and route messages so only what truly needs you reaches you.",
      features: [
        "Email sorting and flagging",
        "Message drafting and routing",
        "Priority filtering",
        "Communication triage"
      ]
    },
    {
      icon: <CheckSquare className="h-12 w-12 text-burnt-orange" />,
      title: "Follow-Through & Execution",
      subtitle: "No more \"Did that get done?\"",
      description: "We track tasks, run follow-ups, coordinate logistics, and keep projects moving so things don't stall.",
      features: [
        "Task tracking and management",
        "Follow-up coordination",
        "Logistics management",
        "Project momentum maintenance"
      ]
    }
  ];

  return (
    <section id="services" className="section-padding bg-white">
      <div className="container-center">
        {/* Section Header */}
        <div className="text-center space-y-6 mb-16 animate-slide-up">
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-olive-brown">
            Support that feels like calm.
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-olive-brown/70 max-w-3xl mx-auto">
            FractionalVa works quietly in the background so your day stops running you.
          </p>
          <p className="text-sm sm:text-base md:text-lg font-medium text-olive-brown">
            You won't feel "more busy." You'll feel more in control.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-gray-light rounded-3xl p-6 sm:p-8 card-shadow hover:bg-gray-medium transition-all duration-500 animate-slide-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="space-y-6">
                {/* Icon and Title */}
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-2xl w-fit group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl font-bold text-olive-brown mb-2">
                      {service.title}
                    </h3>
                    <h4 className="text-base sm:text-lg font-semibold text-burnt-orange mb-4">
                      {service.subtitle}
                    </h4>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm sm:text-base text-olive-brown/70 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <div className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-burnt-orange rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-olive-brown/80 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Hover Effect */}
                <div className="pt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-full h-1 bg-gradient-to-r from-burnt-orange to-olive-brown rounded-full"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16 animate-slide-up">
          <div className="bg-gradient-to-r from-burnt-orange/10 to-olive-brown/10 rounded-3xl p-12">
            <a
              href="#services-detail"
              className="btn-primary"
              aria-label="View what we handle, what we don't, and how we work"
            >
              See Services
            </a>
            <p className="text-olive-brown/60 text-sm mt-6">
              View what we handle, what we don't, and how we work.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;